import React, { useState, useEffect } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  differenceInCalendarDays,
  setYear,
  getYear,
  isBefore,
  getDate,
  getMonth,
  getISOWeek,
} from "date-fns";
import { es } from "date-fns/locale";
import {
  Cake,
  ChevronLeft,
  ChevronRight,
  Calendar as CalIcon,
  Clock,
  X,
  MapPin,
} from "lucide-react";
import "./styles.css";

// --- TIPOS ---
interface BirthdayPerson {
  type: "birthday";
  name: string;
  birthDate: Date;
  date: Date;
  nextBirthday: Date;
  daysLeft: number;
  weeksLeft: number;
  extraDays: number;
  age: number | null;
  zodiac: string;
  isUnknownYear: boolean;
}

interface Holiday {
  type: "holiday";
  name: string;
  country: "CO" | "CA";
  date: Date;
}

type CalendarEvent = BirthdayPerson | Holiday;

// --- BASE DE DATOS FAMILIAR ---
const FAMILY_DATA: Record<string, string> = {
  "Alfonso Nahor Pinilla Baracaldo": "1941-05-15",
  "Alfonso Nahor Pinilla Sanchez": "1977-06-11",
  "Luz Ximena Pinilla Sanchez": "1973-02-25",
  "Ruben Dario Pinilla Sanchez": "1983-03-29",
  "Ricardo Diago Pinilla Sanchez": "1979-02-09",
  "Obdulia Sanchez Romero": "1949-12-30",
  "Ivan Pernett": "1975-10-03",
  "Camilo Bernal Pinilla": "1971-04-18",
  "Nicolas Gustavo Franco Pinilla": "2005-03-11",
  "Gretel Valentina Franco Pinilla": "2010-04-26",
  "Maria Fernanda Pinilla Velandia": "2014-05-04",
  "Andres Felipe Pinilla Velandia": "2008-12-13",
  "Tania Pinilla Berdugo": "2018-05-18",
  "Lucía Pinilla Ruiz": "2020-03-25",
  "Jeronimo Veloza Garzon": "2021-02-19",
  "Liliam Gisela Ruiz Tinoco": "1982-01-17",
  "Laura Ximena Garzón Pinilla": "1992-01-03",
  "Diana Marcela Ruiz Tinoco": "1989-12-28",
  "Miguel Antonio Ruiz Molina": "1951-11-26",
  "Miguel Fernando Ruiz Tinoco": "1977-04-01",
  "Maria Fernanda Ruiz Rivera": "2011-04-09",
  "Miguel Angel Ruiz Rivera": "2016-01-16",
  "Viviana Berdugo": "1983-04-22",
  "Mariela Velandia (Mary)": "1979-12-23",
  "Gustavo Franco Carbonell": "1957-03-14",
  "Mariana Martinez Ruiz": "2014-10-08",
  "Oscar Veloza": "1984-02-25",
  "Nelson Martínez": "1985-04-20",
  "Sandra Patricia Triana": "1970-06-04",
  "Rosa Celignia Pinilla": "1943-02-02",
  "Oscar Niño Pinilla": "1900-02-12",
  Xiomara: "1900-02-15",
  "Roberto Escamilla Pinilla": "1900-02-27",
  "Zoraida Pinilla": "1978-03-08",
  "Carolina Escamilla Pinilla": "1900-03-09",
  "Consuelo Pinilla Baracaldo": "1900-04-03",
  "Cristian Bernal Pinilla": "1976-05-07",
  "Carolina Pinilla": "1900-06-06",
  "Hortensia Pinilla": "1900-06-22",
  "Flor Marina Pinilla": "1946-06-25",
  "Antonio Escamilla Pinilla": "1900-06-28",
  "Margarita Pinilla Baracaldo": "1900-06-30",
  "Alberto Niño Farfán": "1900-11-20",
  "Sandra Milena Niño Pinilla": "1900-11-28",
  "Zully Fernandez": "1900-12-26",
  "Gloria Sanchez": "1943-06-12",
  "Mario José Sánchez Romero": "1945-07-30",
  "Juan Ignacio Sánchez": "1960-01-31",
  "Teresita de Jesús Sánchez": "1967-10-15",
  "Luis Sarmiento (Tico)": "1950-01-24",
  "Paola Rueda": "1986-09-26",
  "Florecita Sánchez": "1955-12-20",
  "Janeth (Esposa Juan)": "1900-10-24",
  "Mario René Sánchez": "1900-10-21",
  "Gabriel Ferro": "1900-09-11",
  "Gloria del Pilar": "1900-08-23",
  "Guillermo Pinilla Pinilla": "1966-10-17",
  "Claudia Helena Bernal Pinilla": "1979-05-23",
};

// --- FESTIVOS 2025 y 2026 ---
const HOLIDAYS_RAW = [
  // 2025 CO
  { d: "2025-01-01", n: "Año Nuevo", c: "CO" },
  { d: "2025-01-06", n: "Reyes Magos", c: "CO" },
  { d: "2025-03-24", n: "San José", c: "CO" },
  { d: "2025-04-13", n: "Domingo Ramos", c: "CO" },
  { d: "2025-04-17", n: "Jueves Santo", c: "CO" },
  { d: "2025-04-18", n: "Viernes Santo", c: "CO" },
  { d: "2025-05-01", n: "Día Trabajo", c: "CO" },
  { d: "2025-06-02", n: "Ascensión", c: "CO" },
  { d: "2025-06-23", n: "Corpus Christi", c: "CO" },
  { d: "2025-06-30", n: "Sagrado Corazón", c: "CO" },
  { d: "2025-07-20", n: "Independencia", c: "CO" },
  { d: "2025-08-07", n: "Batalla Boyacá", c: "CO" },
  { d: "2025-08-18", n: "Asunción", c: "CO" },
  { d: "2025-10-13", n: "Día Raza", c: "CO" },
  { d: "2025-11-03", n: "Todos Santos", c: "CO" },
  { d: "2025-11-17", n: "Indep. Cartagena", c: "CO" },
  { d: "2025-12-08", n: "Inmaculada", c: "CO" },
  { d: "2025-12-25", n: "Navidad", c: "CO" },
  // 2025 CA
  { d: "2025-01-01", n: "New Year's", c: "CA" },
  { d: "2025-02-17", n: "Louis Riel Day", c: "CA" },
  { d: "2025-04-18", n: "Good Friday", c: "CA" },
  { d: "2025-05-19", n: "Victoria Day", c: "CA" },
  { d: "2025-07-01", n: "Canada Day", c: "CA" },
  { d: "2025-08-04", n: "Terry Fox Day", c: "CA" },
  { d: "2025-09-01", n: "Labour Day", c: "CA" },
  { d: "2025-09-30", n: "Truth & Reconciliation", c: "CA" },
  { d: "2025-10-13", n: "Thanksgiving", c: "CA" },
  { d: "2025-11-11", n: "Remembrance", c: "CA" },
  { d: "2025-12-25", n: "Christmas", c: "CA" },
  { d: "2025-12-26", n: "Boxing Day", c: "CA" },
  // 2026 CO
  { d: "2026-01-01", n: "Año Nuevo", c: "CO" },
  { d: "2026-01-12", n: "Reyes Magos", c: "CO" },
  { d: "2026-03-23", n: "San José", c: "CO" },
  { d: "2026-03-29", n: "Domingo Ramos", c: "CO" },
  { d: "2026-04-02", n: "Jueves Santo", c: "CO" },
  { d: "2026-04-03", n: "Viernes Santo", c: "CO" },
  { d: "2026-05-01", n: "Día Trabajo", c: "CO" },
  { d: "2026-05-18", n: "Ascensión", c: "CO" },
  { d: "2026-06-08", n: "Corpus Christi", c: "CO" },
  { d: "2026-06-15", n: "Sagrado Corazón", c: "CO" },
  { d: "2026-06-29", n: "San Pedro", c: "CO" },
  { d: "2026-07-20", n: "Independencia", c: "CO" },
  { d: "2026-08-07", n: "Batalla Boyacá", c: "CO" },
  { d: "2026-08-17", n: "Asunción", c: "CO" },
  { d: "2026-10-12", n: "Día Raza", c: "CO" },
  { d: "2026-11-02", n: "Todos Santos", c: "CO" },
  { d: "2026-11-16", n: "Indep. Cartagena", c: "CO" },
  { d: "2026-12-08", n: "Inmaculada", c: "CO" },
  { d: "2026-12-25", n: "Navidad", c: "CO" },
  // 2026 CA
  { d: "2026-01-01", n: "New Year's", c: "CA" },
  { d: "2026-02-16", n: "Louis Riel Day", c: "CA" },
  { d: "2026-04-03", n: "Good Friday", c: "CA" },
  { d: "2026-05-18", n: "Victoria Day", c: "CA" },
  { d: "2026-07-01", n: "Canada Day", c: "CA" },
  { d: "2026-08-03", n: "Terry Fox Day", c: "CA" },
  { d: "2026-09-07", n: "Labour Day", c: "CA" },
  { d: "2026-09-30", n: "Truth & Reconciliation", c: "CA" },
  { d: "2026-10-12", n: "Thanksgiving", c: "CA" },
  { d: "2026-11-11", n: "Remembrance", c: "CA" },
  { d: "2026-12-25", n: "Christmas", c: "CA" },
  { d: "2026-12-26", n: "Boxing Day", c: "CA" },
];

const getZodiacSign = (date: Date): string => {
  const day = getDate(date);
  const month = getMonth(date) + 1;
  if ((month === 3 && day >= 21) || (month === 4 && day <= 19))
    return "Aries ♈";
  if ((month === 4 && day >= 20) || (month === 5 && day <= 20))
    return "Tauro ♉";
  if ((month === 5 && day >= 21) || (month === 6 && day <= 20))
    return "Géminis ♊";
  if ((month === 6 && day >= 21) || (month === 7 && day <= 22))
    return "Cáncer ♋";
  if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) return "Leo ♌";
  if ((month === 8 && day >= 23) || (month === 9 && day <= 22))
    return "Virgo ♍";
  if ((month === 9 && day >= 23) || (month === 10 && day <= 22))
    return "Libra ♎";
  if ((month === 10 && day >= 23) || (month === 11 && day <= 21))
    return "Escorpio ♏";
  if ((month === 11 && day >= 22) || (month === 12 && day <= 21))
    return "Sagitario ♐";
  if ((month === 12 && day >= 22) || (month === 1 && day <= 19))
    return "Capricornio ♑";
  if ((month === 1 && day >= 20) || (month === 2 && day <= 18))
    return "Acuario ♒";
  if ((month === 2 && day >= 19) || (month === 3 && day <= 20))
    return "Piscis ♓";
  return "";
};

const processBirthdays = (): BirthdayPerson[] => {
  const today = new Date();
  const currentYear = getYear(today);
  return Object.entries(FAMILY_DATA)
    .map(([name, dateStr]) => {
      const [y, m, d] = dateStr.split("-").map(Number);
      const birthDate = new Date(y, m - 1, d);
      let nextBirthday = setYear(birthDate, currentYear);
      if (isBefore(nextBirthday, today) && !isSameDay(nextBirthday, today)) {
        nextBirthday = setYear(birthDate, currentYear + 1);
      }
      const daysLeft = differenceInCalendarDays(nextBirthday, today);
      return {
        type: "birthday" as const,
        name,
        birthDate,
        date: nextBirthday,
        nextBirthday,
        daysLeft,
        weeksLeft: Math.floor(daysLeft / 7),
        extraDays: daysLeft % 7,
        age: y !== 1900 ? getYear(nextBirthday) - y : null,
        zodiac: getZodiacSign(birthDate),
        isUnknownYear: y === 1900,
      } as BirthdayPerson;
    })
    .sort((a, b) => a.daysLeft - b.daysLeft);
};

const processHolidays = (): Holiday[] => {
  return HOLIDAYS_RAW.map((h) => {
    const [y, m, d] = h.d.split("-").map(Number);
    return {
      type: "holiday" as const,
      name: h.n,
      country: h.c as "CO" | "CA",
      date: new Date(y, m - 1, d),
    } as Holiday;
  });
};

const allBirthdays = processBirthdays();
const allHolidays = processHolidays();

// --- COMPONENTES ---

const GlassCard: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = "" }) => (
  <div className={`glass-card ${className}`}>{children}</div>
);

const WorldClocks = () => {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  const formatTime = (tz: string) =>
    new Intl.DateTimeFormat("es-CO", {
      timeZone: tz,
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(time);

  return (
    <div className="world-clocks">
      <div className="clock-card">
        <span className="clock-time">{formatTime("America/Bogota")}</span>
        <span className="clock-label">
          <MapPin size={12} /> Colombia
        </span>
      </div>
      <div className="clock-card">
        <span className="clock-time">{formatTime("America/Winnipeg")}</span>
        <span className="clock-label">
          <MapPin size={12} /> Manitoba
        </span>
      </div>
    </div>
  );
};

const DayDetailsModal: React.FC<{
  date: Date | null;
  events: CalendarEvent[];
  onClose: () => void;
}> = ({ date, events, onClose }) => {
  if (!date || events.length === 0) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <X />
        </button>
        <h2 className="modal-title">
          {format(date, "EEEE d 'de' MMMM", { locale: es })}
        </h2>
        <div className="modal-info">
          {events.map((evt, idx) => (
            <div key={idx} className="modal-event-item">
              {evt.type === "birthday" ? (
                <>
                  <span style={{ fontSize: "1.5rem" }}>🎂</span>
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontWeight: "bold" }}>{evt.name}</div>
                    <div style={{ fontSize: "0.8rem", opacity: 0.8 }}>
                      {evt.isUnknownYear
                        ? "Cumpleaños"
                        : `Cumple ${evt.age} años`}{" "}
                      • {evt.zodiac}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <span className="flag-icon">
                    {evt.country === "CO" ? "🇨🇴" : "🇨🇦"}
                  </span>
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontWeight: "bold" }}>{evt.name}</div>
                    <div style={{ fontSize: "0.8rem", opacity: 0.8 }}>
                      Festivo {evt.country === "CO" ? "Colombia" : "Manitoba"}
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Calendar: React.FC<{
  onSelectDay: (date: Date, events: CalendarEvent[]) => void;
}> = ({ onSelectDay }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);

  const rows = [];
  let days = [];
  let day = startDate;

  while (day <= endDate) {
    const isoWeek = getISOWeek(day);
    days.push(
      <div key={`iso-${day}`} className="iso-week-cell">
        W{isoWeek}
      </div>
    );

    for (let i = 0; i < 7; i++) {
      const cloneDay = day;
      const dayBirthdays = allBirthdays.filter((p) =>
        isSameDay(p.nextBirthday, cloneDay)
      );
      const dayHolidays = allHolidays.filter((h) =>
        isSameDay(h.date, cloneDay)
      );
      const dayEvents = [...dayBirthdays, ...dayHolidays];
      const isCurrentMonth = isSameMonth(day, monthStart);
      const isToday = isSameDay(day, new Date());

      days.push(
        <div
          className={`cell ${
            !isCurrentMonth ? "disabled" : isToday ? "selected" : ""
          }`}
          key={day.toString()}
          onClick={() =>
            dayEvents.length > 0 && onSelectDay(cloneDay, dayEvents)
          }
          style={{ cursor: dayEvents.length > 0 ? "pointer" : "default" }}
        >
          <span className="number">{format(day, "d")}</span>
          <div className="dots-container">
            {dayBirthdays.map((b, idx) => (
              <div
                key={`b-${idx}`}
                className="event-dot birthday"
                title={b.name}
              >
                {b.name.split(" ")[0]}
              </div>
            ))}
            {dayHolidays.map((h, idx) => (
              <div
                key={`h-${idx}`}
                className={`event-dot holiday-${h.country.toLowerCase()}`}
                title={h.name}
              >
                {h.country === "CO" ? "🇨🇴" : "🇨🇦"} {h.name}
              </div>
            ))}
          </div>
        </div>
      );
      day = addDays(day, 1);
    }
    rows.push(
      <div className="row" key={day.toString()}>
        {days}
      </div>
    );
    days = [];
  }

  return (
    <GlassCard className="calendar-container">
      <div className="glass-header">
        <CalIcon size={20} /> Calendario
      </div>
      <div className="calendar-header">
        <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
          <ChevronLeft color="white" />
        </button>
        <span className="month-title">
          {format(currentMonth, "MMMM yyyy", { locale: es }).toUpperCase()}
        </span>
        <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
          <ChevronRight color="white" />
        </button>
      </div>
      <div className="days-row">
        <div className="iso-week-header">#</div>
        {["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"].map((d) => (
          <div className="day-name" key={d}>
            {d}
          </div>
        ))}
      </div>
      <div className="calendar-body">{rows}</div>
    </GlassCard>
  );
};

const UpcomingList: React.FC<{
  onSelectPerson: (p: BirthdayPerson) => void;
}> = ({ onSelectPerson }) => {
  return (
    <GlassCard className="upcoming-container">
      <div className="glass-header">
        <Clock size={20} /> Próximos
      </div>
      <div className="list-scroll">
        {allBirthdays.slice(0, 6).map((person, index) => (
          <div
            key={index}
            className="list-item"
            onClick={() => onSelectPerson(person)}
            style={{ cursor: "pointer" }}
          >
            <div className="list-icon">
              <Cake size={24} color="#fff" />
            </div>
            <div className="list-info">
              <h3 className="person-name">{person.name}</h3>
              <p className="person-date">
                {format(person.nextBirthday, "d 'de' MMMM", { locale: es })}
              </p>
            </div>
            <div className="countdown-badge">
              {person.daysLeft === 0 ? (
                <span className="today-text">¡HOY!</span>
              ) : (
                <div className="time-units">
                  <div className="unit">
                    <span className="val">{person.weeksLeft}</span>
                    <span className="label">Sem</span>
                  </div>
                  <div className="unit">
                    <span className="val">{person.extraDays}</span>
                    <span className="label">Días</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </GlassCard>
  );
};

export default function App() {
  const [modalData, setModalData] = useState<{
    date: Date;
    events: CalendarEvent[];
  } | null>(null);

  return (
    <div className="app-container">
      <div className="background-blobs">
        <div className="blob blob-1"></div>
        <div className="blob blob-2"></div>
        <div className="blob blob-3"></div>
      </div>
      <div className="content-wrapper">
        <h1 className="main-title">Familia Sánchez & Pinilla</h1>
        <p className="subtitle">Celebrando la vida juntos</p>
        <WorldClocks />
        <div className="grid-layout">
          <UpcomingList
            onSelectPerson={(p) =>
              setModalData({ date: p.nextBirthday, events: [p] })
            }
          />
          <Calendar
            onSelectDay={(d, e) => setModalData({ date: d, events: e })}
          />
        </div>
      </div>
      {modalData && (
        <DayDetailsModal
          date={modalData.date}
          events={modalData.events}
          onClose={() => setModalData(null)}
        />
      )}
    </div>
  );
}
